/**
 * Mutations that write invoice-preview lines back onto the work order itself
 * (#1959). The generated invoice later copies these 1:1 — there is no
 * separate billing draft while the work order is still open.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { saveServiceDetails, type ServiceDetailsBase } from '@/components/workorders/billing/serviceDetailsConcurrency';
import { api } from '@/lib/api';
import { queryKeys } from './queryKeys';
import type { WorkOrderItem } from './useWorkOrders';
import type {
  ServiceDetailsPayload,
  WorkOrderConsumedPart,
} from '@/components/workorders/billing/billingLineSource';

export interface UpdateServiceDetailsVars {
  workOrderId: string;
  bookingServiceId: string;
  data: ServiceDetailsPayload;
  base: ServiceDetailsBase;
}

export interface ManualLineCreateData {
  description: string;
  category?: string;
  partsCostCents: number;
  laborCostCents: number;
  estimatedHours?: number;
}

export interface ManualLineUpdateData {
  description?: string;
  category?: string;
  partsCostCents?: number;
  laborCostCents?: number;
  estimatedHours?: number;
}

export interface ConsumedPartUpdateData {
  quantity?: number;
  unitSellPriceCents?: number;
  discountPercent?: number;
}

const EMPTY_CONSUMED_PARTS: WorkOrderConsumedPart[] = [];

function invalidateWorkOrderBilling(queryClient: ReturnType<typeof useQueryClient>, id: string): void {
  // Same set as useWorkOrders.invalidateWorkOrder so the Työt card and the
  // invoice preview refresh together when hours/prices change.
  void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.detail(id) });
  void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.history(id) });
  void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.lists() });
  void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.stats() });
  void queryClient.invalidateQueries({ queryKey: queryKeys.bookings.detail(id) });
  void queryClient.invalidateQueries({ queryKey: ['invoicePreview', id] });
  void queryClient.invalidateQueries({ queryKey: ['workOrderParts', id] });
}

export function useWorkOrderBillingLines(workOrderId?: string) {
  const queryClient = useQueryClient();

  const consumedPartsQuery = useQuery({
    queryKey: ['workOrderParts', workOrderId ?? ''],
    queryFn: () => {
      if (workOrderId == null || workOrderId.length === 0) return Promise.resolve([]);
      return api.get<WorkOrderConsumedPart[]>(`/work-orders/${workOrderId}/parts`);
    },
    enabled: workOrderId != null && workOrderId.length > 0,
  });

  const updateServiceDetails = useMutation({
    mutationFn: (input: UpdateServiceDetailsVars) => saveServiceDetails(input),
    onError: (_, { workOrderId }) => invalidateWorkOrderBilling(queryClient, workOrderId),
    onSuccess: (_, { workOrderId }) => invalidateWorkOrderBilling(queryClient, workOrderId),
  });

  const addManualLine = useMutation({
    mutationFn: ({
      workOrderId,
      data,
    }: {
      workOrderId: string;
      data: ManualLineCreateData;
    }) => api.post<WorkOrderItem>(`/work-orders/${workOrderId}/items`, data),
    onSuccess: (_, { workOrderId }) => invalidateWorkOrderBilling(queryClient, workOrderId),
  });

  const updateManualLine = useMutation({
    mutationFn: ({
      workOrderId,
      itemId,
      data,
    }: {
      workOrderId: string;
      itemId: string;
      data: ManualLineUpdateData;
    }) => api.patch<WorkOrderItem>(`/work-orders/${workOrderId}/items/${itemId}`, data),
    onSuccess: (_, { workOrderId }) => invalidateWorkOrderBilling(queryClient, workOrderId),
  });

  const deleteManualLine = useMutation({
    mutationFn: ({
      workOrderId,
      itemId,
    }: {
      workOrderId: string;
      itemId: string;
    }) => api.delete(`/work-orders/${workOrderId}/items/${itemId}`),
    onSuccess: (_, { workOrderId }) => invalidateWorkOrderBilling(queryClient, workOrderId),
  });

  const updateConsumedPart = useMutation({
    mutationFn: ({
      workOrderId,
      partId,
      data,
    }: {
      workOrderId: string;
      partId: string;
      data: ConsumedPartUpdateData;
    }) => api.patch(`/work-orders/${workOrderId}/parts/${partId}`, data),
    onSuccess: (_, { workOrderId }) => invalidateWorkOrderBilling(queryClient, workOrderId),
  });

  const deleteConsumedPart = useMutation({
    mutationFn: ({
      workOrderId,
      partId,
    }: {
      workOrderId: string;
      partId: string;
    }) => api.delete(`/work-orders/${workOrderId}/parts/${partId}`),
    onSuccess: (_, { workOrderId }) => invalidateWorkOrderBilling(queryClient, workOrderId),
  });

  const isPending =
    updateServiceDetails.isPending
    || addManualLine.isPending
    || updateManualLine.isPending
    || deleteManualLine.isPending
    || updateConsumedPart.isPending
    || deleteConsumedPart.isPending;

  return {
    updateServiceDetails,
    addManualLine,
    updateManualLine,
    deleteManualLine,
    updateConsumedPart,
    deleteConsumedPart,
    consumedParts: consumedPartsQuery.data ?? EMPTY_CONSUMED_PARTS,
    isPending,
  };
}
